"use client";

import { useState } from "react";
import { Icon } from "@templates/design-system";
import { WohaMedia } from "../WohaMedia/WohaMedia";
import type { IVideoPlayerProps } from "./VideoPlayer.props";

export const VideoPlayer = ({
  banner,
  directVideo,
  source,
  vimeoVideo,
  youtubeVideo,
}: IVideoPlayerProps) => {
  const video =
    source === "direct" ? directVideo : source === "youtube" ? youtubeVideo : vimeoVideo;
  const [playing, setPlaying] = useState(false);
  if (!video) return null;

  const autoplay = `${video}${video.includes("?") ? "&" : "?"}autoplay=1`;
  const player =
    source === "direct" ? (
      <video
        autoPlay={playing}
        className="absolute inset-0 h-full w-full bg-charcoal object-cover"
        controls
        playsInline
        src={video}
      />
    ) : (
      <iframe
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 h-full w-full"
        loading="lazy"
        src={playing ? autoplay : video}
        title={`${source} video`}
      />
    );

  return (
    <div className="relative aspect-video overflow-hidden rounded-2xl border border-ash bg-charcoal shadow-subtle-2">
      {banner && !playing ? (
        <button
          aria-label="Play video"
          className="group absolute inset-0 block h-full w-full cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-electric-blue"
          onClick={() => setPlaying(true)}
          type="button"
        >
          <WohaMedia
            aspectRatio="16:9"
            className="pointer-events-none h-full rounded-none"
            imageClassName="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.015]"
            media={banner.image}
            sizes="(min-width: 1200px) 1200px, 100vw"
          />
          <span aria-hidden="true" className="absolute inset-0 bg-black/20" />
          <span className="absolute left-1/2 top-1/2 inline-flex size-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-charcoal shadow-md transition-transform duration-200 group-hover:scale-105 group-active:scale-95">
            <Icon className="ml-1 size-6" name="play" />
          </span>
        </button>
      ) : (
        player
      )}
    </div>
  );
};

VideoPlayer.displayName = "Content::VideoPlayer";
